import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Bell,
  LayoutDashboard,
  LineChart,
  Menu,
  Package2,
  Sparkles,
  Settings2,
  ShoppingBag,
  Store,
  Wallet,
  X,
  LogOut,
} from "lucide-react";
import { signOut } from "firebase/auth";
import { auth, db } from "../firebase";
import { useAuth } from "../AuthProvider";
import {
  collection,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  writeBatch,
} from "firebase/firestore";
import {
  getPackageCountdownData,
  getBusinessTypeLabel,
  summarizeStorePackages,
} from "../utils/storePackages";

const AVATAR_FALLBACK = "/avatar.png";
const RENEWAL_WINDOW_DAYS = 3;

function NotificationPanel({ notifications, unread, onMarkAllRead, onOpenProfile }) {
  return (
    <div className="absolute right-0 top-full z-[70] mt-3 w-[320px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div className="font-semibold text-slate-900">Notifications</div>
        {unread > 0 && (
          <button
            onClick={onMarkAllRead}
            className="rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-200"
          >
            Mark all read
          </button>
        )}
      </div>
      <div className="max-h-80 overflow-y-auto">
        {notifications.length === 0 ? (
          <div className="p-5 text-sm text-slate-500">No notifications yet.</div>
        ) : (
          notifications.map((n) => (
            <button
              key={n.id}
              type="button"
              className={`block w-full border-b px-4 py-3 text-left last:border-b-0 ${
                n.read ? "bg-white" : "bg-blue-50"
              }`}
              onClick={() => {
                if (n.type === "kyc") onOpenProfile();
              }}
            >
              <div className="text-sm font-semibold text-slate-900">
                {n.status === "approved"
                  ? "Approved"
                  : n.status === "declined"
                    ? "Declined"
                    : "Update"}
              </div>
              <div className="mt-1 text-sm text-slate-600">{n.message}</div>
              <div className="mt-1 text-xs text-slate-400">
                {n.createdAt?.seconds
                  ? new Date(n.createdAt.seconds * 1000).toLocaleString()
                  : ""}
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

export default function Navbar() {
  const { user, userData } = useAuth();
  const [localUserData, setLocalUserData] = useState(userData);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [avatarSrc, setAvatarSrc] = useState(AVATAR_FALLBACK);
  const [, setCountdownTick] = useState(Date.now());

  const navigate = useNavigate();
  const notifRef = useRef(null);

  useEffect(() => {
    if (!user?.uid) {
      setLocalUserData(null);
      return;
    }
    if (userData) {
      setLocalUserData(userData);
    }
    const unsub = onSnapshot(doc(db, "users", user.uid), (snap) => {
      setLocalUserData(snap.exists() ? snap.data() : null);
    });
    return () => unsub();
  }, [user?.uid, userData]);

  useEffect(() => {
    const url = (localUserData?.profilePicUrl || "").trim();
    setAvatarSrc(url || AVATAR_FALLBACK);
  }, [localUserData]);

  useEffect(() => {
    if (!user?.uid) return;
    const q = query(
      collection(db, "users", user.uid, "notifications"),
      orderBy("createdAt", "desc"),
      limit(10),
    );
    const unsub = onSnapshot(q, (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setNotifications(rows);
      setUnread(rows.filter((row) => !row.read).length);
    });
    return () => unsub();
  }, [user?.uid]);

  useEffect(() => {
    const handleClick = (event) => {
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setCountdownTick(Date.now());
    }, 60 * 1000);
    return () => window.clearInterval(timer);
  }, []);

  const markAllAsRead = async () => {
    if (!user?.uid) return;
    const snap = await getDocs(collection(db, "users", user.uid, "notifications"));
    const batch = writeBatch(db);
    snap.docs.forEach((row) => {
      if (!row.data().read) {
        batch.update(doc(db, "users", user.uid, "notifications", row.id), {
          read: true,
        });
      }
    });
    await batch.commit();
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/login");
  };

  const displayName =
    localUserData?.fullName?.trim() ||
    localUserData?.displayName?.trim() ||
    user?.displayName?.trim() ||
    (user?.email ? user.email.split("@")[0] : "Shop Owner");

  const avatarInitial = (displayName || "I").charAt(0).toUpperCase();
  const packageState = useMemo(
    () => summarizeStorePackages(localUserData || {}),
    [localUserData],
  );
  const active = packageState.hasActivePackage;
  const activePackage = packageState.latestPackage || packageState.summary || null;
  const packageCountdown = active ? getPackageCountdownData(activePackage || {}) : null;
  const renewalOpen =
    !!packageCountdown &&
    !packageCountdown.isExpired &&
    packageCountdown.remainingMs <= RENEWAL_WINDOW_DAYS * 24 * 60 * 60 * 1000;

  const navItems = useMemo(() => {
    const items = [
      { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
      { to: "/store-packages", label: "Store Packages", icon: Package2 },
    ];
    if (active) {
      items.push(
        { to: "/insights", label: "Insights", icon: LineChart },
        { to: "/manage-listing", label: "Manage Listings", icon: ShoppingBag },
        { to: "/sales-orders", label: "Sales Orders", icon: Wallet },
        { to: "/affiliate-settlements", label: "Affiliate Payouts", icon: Wallet },
        { to: "/store-sponsorships", label: "Shop Sponsor", icon: Sparkles },
        { to: "/store", label: "My Store", icon: Store },
      );
    }
    items.push({ to: "/profile", label: "Profile", icon: Settings2 });
    return items;
  }, [active]);

  const openProfile = () => {
    setNotifOpen(false);
    setMobileOpen(false);
    navigate("/profile");
  };

  const SidebarLink = ({ to, label, icon: Icon }) => (
    <NavLink
      to={to}
      className={({ isActive }) =>
        [
          "flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-semibold transition",
          isActive
            ? "bg-white text-slate-950 shadow-md"
            : "text-slate-300 hover:bg-white/10 hover:text-white",
        ].join(" ")
      }
      onClick={() => setMobileOpen(false)}
    >
      <Icon className="h-5 w-5" />
      <span>{label}</span>
    </NavLink>
  );

  const SidebarBody = ({ mobile = false }) => (
    <div className={`flex h-full flex-col ${mobile ? "bg-slate-950" : ""}`}>
      <div className="border-b border-white/10 px-5 py-5">
        <button
          type="button"
          onClick={() => {
            navigate("/dashboard");
            setMobileOpen(false);
          }}
          className="flex items-center gap-3"
        >
          <img
            src="/user-portal-logo.png"
            alt="Ceylon Web Mall"
            className="h-11 w-11 rounded-2xl shadow-lg"
          />
          <div className="text-left">
            <div className="text-2xl font-black text-white">Shop Owner</div>
            <div className="text-sm text-slate-300 truncate max-w-[180px]">
              {displayName}
            </div>
          </div>
        </button>

        <div className="mt-4 rounded-2xl bg-white/10 px-4 py-3 text-sm text-slate-200">
          <div className="font-semibold text-white">
            {active
              ? `Active package: ${packageState.summary.name || packageState.summary.packageId}`
              : "No active package yet"}
          </div>
          <div className="mt-1 text-slate-300">
            {active
              ? `Your ${getBusinessTypeLabel(localUserData?.storeBusinessType || "").toLowerCase()} store is unlocked and ready to grow.`
              : "Buy a package first, then complete your store setup."}
          </div>
          {active && packageCountdown ? (
            <div className="mt-3 rounded-2xl border border-white/10 bg-slate-900/50 px-3 py-3">
              <div className="flex items-center justify-between gap-3">
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                  Package countdown
                </div>
                <div
                  className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em] ${
                    renewalOpen
                      ? "bg-amber-300 text-slate-950"
                      : "bg-emerald-400/20 text-emerald-200"
                  }`}
                >
                  {renewalOpen ? "Renewal open" : "Active"}
                </div>
              </div>
              <div className="mt-2 text-xl font-black text-white">
                {packageCountdown.short}
              </div>
              <div className="mt-1 text-xs text-slate-400">
                Ends{" "}
                {packageCountdown.expiryMs
                  ? new Date(packageCountdown.expiryMs).toLocaleString()
                  : "soon"}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex-1 space-y-2 overflow-y-auto px-4 py-5">
        {navItems.map((item) => (
          <SidebarLink key={item.to} {...item} />
        ))}
      </div>

      <div className="border-t border-white/10 px-4 py-4">
        <div className="flex items-center gap-3 rounded-2xl bg-white/5 p-3">
          {avatarSrc !== AVATAR_FALLBACK ? (
            <img
              src={avatarSrc}
              alt="Profile"
              className="h-11 w-11 rounded-2xl object-cover"
              onError={() => setAvatarSrc(AVATAR_FALLBACK)}
            />
          ) : (
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/15 font-bold text-white">
              {avatarInitial}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="truncate font-semibold text-white">{displayName}</div>
            <div className="truncate text-xs text-slate-400">
              {user?.email || "Shop owner account"}
            </div>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="rounded-xl bg-white/10 p-2 text-slate-200 hover:bg-white/20"
            aria-label="Logout"
          >
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-72 overflow-hidden bg-slate-950 lg:flex">
        <SidebarBody />
      </aside>

      <div className="fixed inset-x-0 top-0 z-50 border-b border-slate-200 bg-white/90 backdrop-blur lg:hidden">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="rounded-xl border p-2 text-slate-700"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="flex items-center gap-2"
            >
              <img
                src="/user-portal-logo.png"
                alt="Ceylon Web Mall"
                className="h-10 w-10 rounded-2xl shadow"
              />
              <div className="text-left">
                <div className="text-lg font-black text-slate-900">Shop Owner</div>
                <div className="max-w-[130px] truncate text-xs text-slate-500">
                  {displayName}
                </div>
              </div>
            </button>
          </div>

          <div className="flex items-center gap-2">
            <div className="relative" ref={notifRef}>
              <button
                type="button"
                onClick={() => setNotifOpen((value) => !value)}
                className="relative rounded-xl border p-2 text-slate-700"
                aria-label="Notifications"
              >
                <Bell className="h-5 w-5" />
                {unread > 0 && (
                  <span className="absolute -right-1 -top-1 rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                    {unread}
                  </span>
                )}
              </button>
              {notifOpen && (
                <NotificationPanel
                  notifications={notifications}
                  unread={unread}
                  onMarkAllRead={markAllAsRead}
                  onOpenProfile={openProfile}
                />
              )}
            </div>

            <button
              type="button"
              onClick={openProfile}
              className="rounded-xl border p-1"
              aria-label="Profile"
            >
              {avatarSrc !== AVATAR_FALLBACK ? (
                <img
                  src={avatarSrc}
                  alt="Profile"
                  className="h-9 w-9 rounded-xl object-cover"
                  onError={() => setAvatarSrc(AVATAR_FALLBACK)}
                />
              ) : (
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 font-bold text-slate-700">
                  {avatarInitial}
                </div>
              )}
            </button>
          </div>
        </div>
      </div>

      <div
        className={`fixed inset-0 z-[60] bg-black/50 transition lg:hidden ${
          mobileOpen ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
        onClick={() => setMobileOpen(false)}
      />
      <aside
        className={`fixed inset-y-0 left-0 z-[70] w-[88vw] max-w-[320px] transform transition lg:hidden ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="absolute right-4 top-4 z-10">
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="rounded-xl bg-white/10 p-2 text-white backdrop-blur"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <SidebarBody mobile />
      </aside>
    </>
  );
}
